<template>
  <div class="wrapper">
    <div>
      <!-- Page Header -->
      <CCard class="mb-4 border-0">
        <CCardBody class="p-4">
          <router-link to="/tenants/EmployeeList" class="small d-inline-block mb-2">&larr; Employees</router-link>
          <CRow class="align-items-center">
            <CCol md="8">
              <h1 class="h4 mb-1 font-weight-bold">
                {{ obj.id ? obj.fullName || "Employee" : "Add employee" }}
              </h1>
              <p class="text-muted mb-0">
                {{ obj.id ? "Personal, payroll and sign-in details" : "Add their details and an initial password so they can see their payslips" }}
              </p>
            </CCol>
            <CCol md="4" class="d-flex justify-content-md-end mt-3 mt-md-0">
              <CButton color="light" class="mr-2" :disabled="saving" @click="cancel">
                Cancel
              </CButton>
              <CButton color="primary" :disabled="saving" @click="submit">
                <CSpinner v-if="saving" size="sm" class="mr-1" />
                {{ obj.id ? "Save" : "Add employee" }}
              </CButton>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CRow>
        <CCol sm="12">
          <CCard class="border-0">
            <CCardBody class="p-4">
              <CForm>
                <h2 class="h6 font-weight-bold mb-4 pb-2 border-bottom">Basic details</h2>
                <CInput
                  label="Full name *"
                  horizontal
                  v-model="obj.fullName"
                  placeholder="As shown on their IC"
                  :is-valid="errors.fullName ? false : null"
                  :invalid-feedback="errors.fullName"
                />

                <CSelect
                  label="Role *"
                  horizontal
                  :options="['Accountant', 'Staff']"
                  :value.sync="obj.appUser.role"
                  required
                />
                <CInput
                  label="Email *"
                  horizontal
                  v-model="obj.email"
                  :readonly="isEmailReadOnly"
                  type="email"
                  placeholder="name@company.com"
                  :description="obj.id ? 'Used to sign in. It can\'t be changed here.' : 'They sign in with this email.'"
                  :is-valid="errors.email ? false : null"
                  :invalid-feedback="errors.email"
                />
                <CInput
                  label="Date of birth *"
                  horizontal
                  type="date"
                  v-model="obj.dateOfBirth"
                  description="Used to work out EPF, SOCSO and EIS rates by age"
                  :is-valid="errors.dateOfBirth ? false : null"
                  :invalid-feedback="errors.dateOfBirth"
                />

                <h2 class="h6 font-weight-bold mt-5 mb-4 pb-2 border-bottom">Pay</h2>
                <CInput
                  label="Basic salary"
                  horizontal
                  type="number"
                  step="0.01"
                  min="0"
                  inputmode="decimal"
                  prepend="RM"
                  v-model="obj.basicSalary"
                  description="Monthly, before deductions. Added to new payrolls automatically."
                />

                <h2 class="h6 font-weight-bold mt-5 mb-4 pb-2 border-bottom">Statutory numbers</h2>
                <CInput id="employee-epf" horizontal v-model="obj.epfNumber" inputmode="numeric">
                  <template #label>
                    <label for="employee-epf" class="col-sm-3 col-form-label">EPF number <HrGlossaryTip term="EPF" /></label>
                  </template>
                </CInput>
                <CInput id="employee-socso" horizontal v-model="obj.socsoNumber">
                  <template #label>
                    <label for="employee-socso" class="col-sm-3 col-form-label">SOCSO number <HrGlossaryTip term="SOCSO" /></label>
                  </template>
                </CInput>
                <CInput id="employee-tax" horizontal v-model="obj.taxNumber" description="Income tax number from LHDN">
                  <template #label>
                    <label for="employee-tax" class="col-sm-3 col-form-label">Tax number <HrGlossaryTip term="PCB" /></label>
                  </template>
                </CInput>
                <CRow form class="form-group">
                  <CCol tag="label" sm="3" class="col-form-label">
                    Opted out of EPF
                  </CCol>
                  <CCol sm="9">
                    <CSwitch
                      class="mr-1"
                      color="primary"
                      :checked.sync="obj.isEPFOptedOut"
                      aria-label="Opted out of EPF"
                    />
                    <small class="form-text text-muted">Only for employees aged 55 to 60 who chose to stop EPF.</small>
                  </CCol>
                </CRow>
                <CRow form class="form-group">
                  <CCol tag="label" sm="3" class="col-form-label">
                    Enrolled in LINDUNG 24 Jam (SKBBK)
                  </CCol>
                  <CCol sm="9">
                    <CSwitch
                      class="mr-1"
                      color="primary"
                      :checked.sync="obj.skbbkEnrolled"
                      aria-label="Enrolled in LINDUNG 24 Jam"
                    />
                    <small class="form-text text-muted">Opt-in for local employees; mandatory for foreign employees. Adds a 0.75% employee contribution (max RM44.65).</small>
                  </CCol>
                </CRow>

                <h2 class="h6 font-weight-bold mt-5 mb-4 pb-2 border-bottom">Bank details</h2>
                <CInput
                  label="Bank"
                  horizontal
                  v-model="obj.bankName"
                  placeholder="For example, Maybank"
                />
                <CInput
                  label="Account name"
                  horizontal
                  v-model="obj.bankAccountName"
                  description="As registered with the bank"
                />
                <CInput
                  label="Account number"
                  horizontal
                  inputmode="numeric"
                  v-model="obj.bankAccountNumber"
                  description="Where their salary is paid"
                />

                <h2 class="h6 font-weight-bold mt-5 mb-4 pb-2 border-bottom">Employment</h2>
                <CInput
                  label="Join date"
                  horizontal
                  type="date"
                  v-model="obj.joinDate"
                />
                <CInput
                  label="Resign date"
                  horizontal
                  type="date"
                  v-model="obj.resignDate"
                  description="Leave empty while they still work here."
                />
                <CRow form class="form-group">
                  <CCol tag="label" sm="3" class="col-form-label">
                    Employment status
                  </CCol>
                  <CCol sm="9">
                    <CSelect
                      :options="employmentStatusOptions"
                      :value.sync="obj.employmentStatus"
                      aria-label="Employment status"
                      class="mb-0"
                    />
                    <small class="form-text text-muted">
                      <span v-if="obj.employmentStatus === 'Active'">Currently works here</span>
                      <span v-else-if="obj.employmentStatus === 'Inactive'">No longer works here (resigned or left)</span>
                      <span v-else-if="obj.employmentStatus === 'OnLeave'">On temporary leave</span>
                    </small>
                  </CCol>
                </CRow>
                <CAlert v-if="obj.resignDate" color="info">
                  To rehire this employee later, clear the resign date and set a new join date.
                </CAlert>

                <h2 class="h6 font-weight-bold mt-5 mb-4 pb-2 border-bottom">Sign-in and password</h2>
                <CRow form class="form-group" v-if="obj.id">
                  <CCol tag="label" sm="3" class="col-form-label">
                    Set a new password
                  </CCol>
                  <CCol sm="9">
                    <CSwitch
                      class="mr-1"
                      color="primary"
                      :checked.sync="isResetPassword"
                      aria-label="Set a new password"
                    />
                    <small class="form-text text-muted">
                      Turn on to give them a new password
                    </small>
                  </CCol>
                </CRow>
                <CAlert v-if="!obj.id" color="info" class="mb-3">
                  Set a first password for them. They can change it after they sign in.
                </CAlert>
                <CInput
                  :label="obj.id ? 'New password *' : 'Password *'"
                  horizontal
                  type="password"
                  autocomplete="new-password"
                  v-model="plainPassword"
                  v-if="isResetPassword"
                  description="At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol"
                  :is-valid="errors.password ? false : null"
                  :invalid-feedback="errors.password"
                />
                <CRow form class="form-group">
                  <CCol tag="label" sm="3" class="col-form-label">
                    Can sign in
                  </CCol>
                  <CCol sm="9">
                    <CSwitch
                      class="mr-1"
                      color="primary"
                      :checked.sync="obj.isEnable"
                      aria-label="Can sign in"
                    />
                    <small class="form-text text-muted">
                      {{ obj.isEnable ? "They can sign in to see their payslips and claims" : "They can't sign in" }}
                    </small>
                  </CCol>
                </CRow>
              </CForm>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  </div>
</template>

<script>
import ProfileApi from "../../lib/profileApi";
import HrGlossaryTip from "../components/HrGlossaryTip";

export default {
  name: "User",
  components: { HrGlossaryTip },
  data: () => {
    return {
      isResetPassword: false,
      plainPassword: "",
      isEmailReadOnly: false,

      saving: false,
      errors: {},
      api: new ProfileApi(),
      employmentStatusOptions: [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
        { value: 'OnLeave', label: 'On leave' }
      ],
      obj: {
        fullName: "",
        appUser: {
          role: "Staff",
        },
        email: "",
        dateOfBirth: null,
        basicSalary: null,
        epfNumber: "",
        socsoNumber: "",
        taxNumber: "",
        isEPFOptedOut: false,
        skbbkEnrolled: false,
        bankName: "",
        bankAccountName: "",
        bankAccountNumber: "",
        joinDate: null,
        resignDate: null,
        employmentStatus: 'Active',
        isEnable: true,
      },
      loading: false,
      submitted: false,
    };
  },
  mounted() {
    var self = this;
    self.$store.commit("setPageNavItems", ["HR", "Employees", self.$route.params.id ? "Edit" : "Add"]);
    self.resetObj();
  },

  computed: {
    formString() {
      return JSON.stringify(this.obj, null, 4);
    },
  },
  methods: {
    onSubmit() {
      var self = this;

      // Validation: inline field errors, no toasts
      const errors = {};
      if (!self.obj.fullName || self.obj.fullName.trim() === "") {
        errors.fullName = "Enter the employee's full name.";
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!self.obj.email || self.obj.email.trim() === "") {
        errors.email = "Enter an email address, like name@company.com.";
      } else if (!emailRegex.test(self.obj.email)) {
        errors.email = "Enter an email address like name@company.com.";
      }
      if (!self.obj.dateOfBirth) {
        errors.dateOfBirth = "Enter their date of birth so EPF, SOCSO and EIS are worked out correctly.";
      }
      if (self.isResetPassword) {
        const pw = self.plainPassword || "";
        if (!pw.trim()) {
          errors.password = "Enter a password.";
        } else if (
          pw.length < 8 ||
          !/[A-Z]/.test(pw) ||
          !/[a-z]/.test(pw) ||
          !/[0-9]/.test(pw) ||
          !/[!@#$%^&*(),.?":{}|<>]/.test(pw)
        ) {
          errors.password = "Use at least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol such as !";
        }
      }
      self.errors = errors;
      if (Object.keys(errors).length) {
        self.$nextTick(() => {
          const el = self.$el.querySelector(".is-invalid");
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.focus();
          }
        });
        return;
      }
      if (self.saving) return;

      // For new employees, ensure appUser.role is set
      if (!self.obj.id && (!self.obj.appUser || !self.obj.appUser.role)) {
        self.obj.appUser = { role: "Staff" };
      }

      var profileDto = {
        profile: self.obj,
        isResetPassword: self.isResetPassword,
        plainPassword: self.isResetPassword ? self.plainPassword : null,
        role: self.obj.appUser.role,
      };

      self.saving = true;
      if (!self.obj.id) {
        this.api
          .createProfileEmployee(profileDto)
          .then((response) => {
            self.toast("Success", "Employee added", "success");
            self.$router.push({ path: "/tenants/EmployeeList" });
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't add the employee. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .updateProfileAppUser(profileDto)
          .then((response) => {
            self.toast("Success", "Employee saved", "success");
            self.$router.push({ path: "/tenants/EmployeeList" });
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't save the employee. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },

    // onReset(evt) {
    //   evt.preventDefault();
    //   this.obj = {};
    // },
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning" }[color] || "info";
      this.$toast[kind](message);
    },
    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.isEmailReadOnly = true;
        this.api
          .getProfile(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            self.isResetPassword = false; // Don't reset password by default when editing
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't load the employee. ${helper.getErrorMessage(data)}`, "danger");
          });
      } else {
        self.obj = self.getEmptyObj();
        self.isResetPassword = true; // Require password for new employees
        self.plainPassword = ""; // Clear password field
        self.isEmailReadOnly = false;
      }
    },
    reset() {
      // this.obj = this.getEmptyObj();
      this.resetObj();
      this.submitted = false;
      this.$v.$reset();
    },
    getEmptyObj() {
      return {
        fullName: "",
        email: "",
        appUser: {
          role: "Staff",
        },
        dateOfBirth: null,
        basicSalary: null,
        epfNumber: "",
        socsoNumber: "",
        taxNumber: "",
        isEPFOptedOut: false,
        skbbkEnrolled: false,
        bankName: "",
        bankAccountName: "",
        bankAccountNumber: "",
        joinDate: null,
        resignDate: null,
        employmentStatus: 'Active',
        isEnable: true,
      };
    },
    submit() {
      this.onSubmit();
      this.submitted = true;
    },
    cancel() {
      this.$router.push({ path: "/tenants/EmployeeList" });
    },
  },
};
</script>

<style scoped>
.form-group label {
  font-weight: 500;
}
</style>
